import { makeAutoObservable, runInAction } from "mobx";
import { i18next } from "../i18n";
import { errorMessage } from "../lib/format";
import {
	acceptFriendRequest,
	blockUser,
	declineFriendRequest,
	listBlockedUsers,
	listFriendRequests,
	listFriends,
	removeFriend,
	sendFriendRequest,
	unblockUser,
	type FriendRequestSummary,
	type FriendSummary,
	type OutgoingFriendRequestSummary,
} from "../lib/social";

export class FriendsStore {
	userId: string | null = null;
	friends: FriendSummary[] = [];
	incomingRequests: FriendRequestSummary[] = [];
	outgoingRequests: OutgoingFriendRequestSummary[] = [];
	blocked: FriendSummary[] = [];
	loading = false;
	submitting = false;
	error = "";
	notice = "";

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true });
	}

	get friendCount() {
		return this.friends.length;
	}

	get requestCount() {
		return this.incomingRequests.length;
	}

	get blockedCount() {
		return this.blocked.length;
	}

	async hydrate(userId: string) {
		if (this.userId === userId && (this.loading || this.friends.length || this.incomingRequests.length)) {
			return;
		}
		this.userId = userId;
		await this.refresh();
	}

	reset() {
		this.userId = null;
		this.friends = [];
		this.incomingRequests = [];
		this.outgoingRequests = [];
		this.blocked = [];
		this.loading = false;
		this.submitting = false;
		this.error = "";
		this.notice = "";
	}

	clearFeedback() {
		this.error = "";
		this.notice = "";
	}

	async refresh() {
		this.loading = true;
		this.error = "";
		try {
			const [friends, requests, blocked] = await Promise.all([
				listFriends(),
				listFriendRequests(),
				listBlockedUsers(),
			]);
			runInAction(() => {
				this.friends = friends;
				this.incomingRequests = requests.incoming;
				this.outgoingRequests = requests.outgoing;
				this.blocked = blocked;
			});
		} catch (error) {
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("friends.loadFailed"));
			});
		} finally {
			runInAction(() => {
				this.loading = false;
			});
		}
	}

	async sendRequest(handle: string): Promise<boolean> {
		const normalized = handle.trim().replace(/^@+/, "");
		if (!normalized) {
			this.error = i18next.t("friends.handleRequired");
			return false;
		}
		this.submitting = true;
		this.clearFeedback();
		try {
			const result = await sendFriendRequest(normalized);
			runInAction(() => {
				this.notice =
					result.status === "accepted" ? i18next.t("friends.nowFriends") : i18next.t("friends.requestSent");
			});
			await this.refresh();
			return true;
		} catch (error) {
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("friends.requestFailed"));
			});
			return false;
		} finally {
			runInAction(() => {
				this.submitting = false;
			});
		}
	}

	async accept(id: string): Promise<boolean> {
		this.submitting = true;
		this.clearFeedback();
		try {
			await acceptFriendRequest(id);
			runInAction(() => {
				this.notice = i18next.t("friends.acceptedNotice");
			});
			await this.refresh();
			return true;
		} catch (error) {
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("friends.acceptFailed"));
			});
			return false;
		} finally {
			runInAction(() => {
				this.submitting = false;
			});
		}
	}

	async decline(id: string): Promise<boolean> {
		this.submitting = true;
		this.clearFeedback();
		try {
			await declineFriendRequest(id);
			runInAction(() => {
				this.notice = i18next.t("friends.declinedNotice");
			});
			await this.refresh();
			return true;
		} catch (error) {
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("friends.declineFailed"));
			});
			return false;
		} finally {
			runInAction(() => {
				this.submitting = false;
			});
		}
	}

	async remove(handle: string): Promise<boolean> {
		this.submitting = true;
		this.clearFeedback();
		try {
			await removeFriend(handle);
			runInAction(() => {
				this.friends = this.friends.filter((friend) => friend.handle !== handle);
				this.notice = i18next.t("friends.removedNotice");
			});
			return true;
		} catch (error) {
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("friends.removeFailed"));
			});
			return false;
		} finally {
			runInAction(() => {
				this.submitting = false;
			});
		}
	}

	async block(handle: string): Promise<boolean> {
		this.submitting = true;
		this.clearFeedback();
		try {
			await blockUser(handle);
			runInAction(() => {
				this.notice = i18next.t("friends.blockedNotice");
			});
			// A block also drops the friendship and any pending request server-side, so re-pull
			// instead of patching one list.
			await this.refresh();
			return true;
		} catch (error) {
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("friends.blockFailed"));
			});
			return false;
		} finally {
			runInAction(() => {
				this.submitting = false;
			});
		}
	}

	async unblock(handle: string): Promise<boolean> {
		this.submitting = true;
		this.clearFeedback();
		try {
			await unblockUser(handle);
			runInAction(() => {
				this.blocked = this.blocked.filter((user) => user.handle !== handle);
				this.notice = i18next.t("friends.unblockedNotice");
			});
			return true;
		} catch (error) {
			runInAction(() => {
				this.error = errorMessage(error, i18next.t("friends.unblockFailed"));
			});
			return false;
		} finally {
			runInAction(() => {
				this.submitting = false;
			});
		}
	}
}

export const friendsStore = new FriendsStore();
